import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, PencilSimple, CheckCircle, Package, User, ArrowRight, Receipt, Warning,
  Plus, Trash, MagnifyingGlass, Truck, Calendar, Tag, IdentificationCard,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { PrepaidSettlement } from "../../components/payments/PrepaidSettlement";
import { extraChargeLabel } from "../../lib/labels";

/**
 * Draft Invoice Editor — bottom-sheet modal used by InvoicesPending.
 *
 * Iter 138+: shows customer's primary waste tags, the assigned/completed
 * field staff, service date & vehicle no, and lets office staff append
 * ad-hoc waste items + products (in addition to editing covered weights
 * and toggling extras).
 */
export default function DraftInvoiceEditor({ invoiceId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [weights, setWeights] = useState({}); // waste_type_id -> string kg (COVERED lines)
  const [adhocWaste, setAdhocWaste] = useState([]); // [{waste_type_id, quantity_kg}]
  const [products, setProducts] = useState([]); // [{product_id, quantity}]
  const [extras, setExtras] = useState([]);
  const [distance, setDistance] = useState(0);
  const [notes, setNotes] = useState("");
  const [pickerOpen, setPickerOpen] = useState(null); // "waste" | "product" | null
  const [pickerQuery, setPickerQuery] = useState("");
  // Iter 48 — live preview totals (recomputed server-side, debounced).
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/finance/draft-invoices/${invoiceId}`);
        if (!alive) return;
        setCtx(r.data);
        const w = {};
        for (const line of r.data.current_waste_lines || []) {
          w[line.waste_type_id] = String(line.quantity_kg || "");
        }
        setWeights(w);
        setAdhocWaste((r.data.current_adhoc_waste_lines || []).map((l) => ({
          waste_type_id: l.waste_type_id,
          quantity_kg: Number(l.quantity_kg || 0),
        })));
        setProducts((r.data.current_product_lines || []).map((l) => ({
          product_id: l.product_id,
          quantity: Number(l.quantity || 1),
        })));
        setExtras(r.data.current_extra_charge_ids || []);
        setDistance(Number(r.data.current_distance_km || 0));
        setNotes(r.data.invoice?.field_notes || "");
      } catch (e) {
        toast.error(formatApiError(e));
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [invoiceId]);

  const inv = ctx?.invoice;
  const [prepaid, setPrepaid] = useState(null);
  const srId = inv?.service_request_id || inv?.source_sr_id;
  useEffect(() => {
    if (!srId) return;
    api.get(`/finance/sr-prepaid/${srId}`).then(r => setPrepaid(r.data.prepaid)).catch(() => {});
  }, [srId]);
  const included = ctx?.included_types || [];
  const allWaste = ctx?.all_waste_types || [];
  const allProducts = ctx?.all_products || [];
  const wasteById = useMemo(() => {
    const m = new Map();
    for (const w of allWaste) m.set(w.waste_type_id, w);
    for (const w of included) m.set(w.waste_type_id, w);
    return m;
  }, [allWaste, included]);
  const productById = useMemo(() => {
    const m = new Map();
    for (const p of allProducts) m.set(p.id, p);
    return m;
  }, [allProducts]);

  const payload = useMemo(() => ({
    waste_lines: [
      ...Object.entries(weights)
        .map(([wt, kg]) => ({ waste_type_id: wt, quantity_kg: Number(kg || 0) }))
        .filter((l) => l.quantity_kg > 0),
      ...adhocWaste
        .filter((l) => l.waste_type_id && Number(l.quantity_kg || 0) > 0)
        .map((l) => ({ waste_type_id: l.waste_type_id, quantity_kg: Number(l.quantity_kg) })),
    ],
    product_lines: products
      .filter((p) => p.product_id && Number(p.quantity || 0) > 0)
      .map((p) => ({ product_id: p.product_id, quantity: Number(p.quantity) })),
    extra_charge_ids: extras,
    distance_km: Number(distance || 0),
    notes: notes || null,
  }), [weights, adhocWaste, products, extras, distance, notes]);

  useEffect(() => {
    if (loading) return;
    setPreviewing(true);
    const t = setTimeout(() => {
      api.post(`/finance/draft-invoices/${invoiceId}/preview`, payload)
        .then((r) => setPreview(r.data))
        .catch(() => { /* keep last preview */ })
        .finally(() => setPreviewing(false));
    }, 350);
    return () => { clearTimeout(t); setPreviewing(false); };
  }, [payload, invoiceId, loading]);

  const save = async (approve = false) => {
    setBusy(true);
    try {
      await api.put(`/finance/draft-invoices/${invoiceId}`, payload);
      if (approve) {
        await api.post(`/finance/draft-invoices/${invoiceId}/finalize`, { notes: notes || null });
        toast.success(`Invoice ${inv.invoice_no} finalized`);
      } else {
        toast.success("Draft updated");
      }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const fmtDate = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
    } catch { return String(v); }
  };

  const pickerResults = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (pickerOpen === "waste") {
      const used = new Set(adhocWaste.map((l) => l.waste_type_id));
      return allWaste
        .filter((w) => !used.has(w.waste_type_id))
        .filter((w) => !q
          || (w.waste_name || "").toLowerCase().includes(q)
          || (w.category || "").toLowerCase().includes(q))
        .slice(0, 40);
    }
    if (pickerOpen === "product") {
      const used = new Set(products.map((p) => p.product_id));
      return allProducts
        .filter((p) => !used.has(p.id))
        .filter((p) => !q
          || (p.name || "").toLowerCase().includes(q)
          || (p.sku || "").toLowerCase().includes(q))
        .slice(0, 40);
    }
    return [];
  }, [pickerOpen, pickerQuery, allWaste, allProducts, adhocWaste, products]);

  const addWasteItem = (w) => {
    setAdhocWaste((rows) => [...rows, { waste_type_id: w.waste_type_id, quantity_kg: 1 }]);
    setPickerOpen(null); setPickerQuery("");
  };
  const addProductItem = (p) => {
    setProducts((rows) => [...rows, { product_id: p.id, quantity: 1 }]);
    setPickerOpen(null); setPickerQuery("");
  };
  const removeAdhocWaste = (idx) =>
    setAdhocWaste((rows) => rows.filter((_, i) => i !== idx));
  const removeProduct = (idx) =>
    setProducts((rows) => rows.filter((_, i) => i !== idx));

  const primaryTags = ctx?.primary_waste_tags || [];
  const sr = ctx?.sr_meta || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={onClose} data-testid="draft-editor-modal">
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#E5E2D9] flex items-start justify-between shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Edit draft invoice</div>
            <div className="font-heading text-lg inline-flex items-center gap-1.5">
              <PencilSimple size={16} /> {inv?.invoice_no || "…"}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F7F6F2]" data-testid="draft-editor-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <div className="text-sm text-[#8A8780]">Loading draft…</div>}

          {!loading && ctx && (
            <>
              {/* Customer + package (read-only) */}
              <div className="bg-[#F7F6F2] rounded-md p-3 space-y-1.5" data-testid="draft-editor-customer">
                <div className="flex items-center gap-2 text-sm font-heading">
                  <User size={14} className="text-[#2D5A27]" />
                  {inv?.customer?.name || inv?.customer_name || ctx?.customer?.name || "—"}
                  {ctx?.customer?.customer_code && (
                    <span className="text-[10px] font-mono text-[#8A8780]">
                      · {ctx.customer.customer_code}
                    </span>
                  )}
                </div>
                {(inv?.customer?.phone || ctx?.customer?.phone) && (
                  <div className="text-[11px] text-[#5C5A55] font-mono">
                    {inv?.customer?.phone || ctx.customer.phone}
                  </div>
                )}
                {ctx.package && (
                  <div className="text-[11px] text-[#2D5A27]">
                    <Package size={10} className="inline -mt-0.5 mr-0.5"/>
                    Package: <strong>{ctx.package.name}</strong>
                    {ctx.package.price ? <span className="text-[#8A8780]"> · ₹{ctx.package.price}</span> : null}
                  </div>
                )}

                {/* Primary waste tags */}
                {primaryTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1" data-testid="draft-primary-tags">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] inline-flex items-center gap-0.5">
                      <Tag size={10} /> Primary waste
                    </span>
                    {primaryTags.map((t) => (
                      <span key={t.id}
                            data-testid={`draft-primary-tag-${t.id}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#EFEAD8] text-[#5C4A1E] text-[10px] font-medium">
                        {t.waste_name}
                      </span>
                    ))}
                  </div>
                )}

                {/* SR meta: staff / date / vehicle */}
                {(sr.field_completed_by_staff_name || sr.assigned_staff_name
                  || sr.vehicle_no || sr.completed_at || sr.scheduled_date) && (
                  <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-[#E5E2D9]"
                       data-testid="draft-sr-meta">
                    {(sr.field_completed_by_staff_name || sr.assigned_staff_name) && (
                      <div className="text-[11px] text-[#3A3A38] inline-flex items-center gap-1"
                           data-testid="draft-sr-staff">
                        <IdentificationCard size={12} className="text-[#2D5A27] shrink-0" />
                        <span className="truncate">
                          {sr.field_completed_by_staff_name || sr.assigned_staff_name}
                        </span>
                      </div>
                    )}
                    {(sr.completed_at || sr.scheduled_date) && (
                      <div className="text-[11px] text-[#3A3A38] inline-flex items-center gap-1"
                           data-testid="draft-sr-date">
                        <Calendar size={12} className="text-[#2D5A27] shrink-0" />
                        <span className="truncate">
                          {sr.completed_at
                            ? `Completed ${fmtDate(sr.completed_at)}`
                            : `Scheduled ${fmtDate(sr.scheduled_date)}`}
                        </span>
                      </div>
                    )}
                    {sr.vehicle_no && (
                      <div className="text-[11px] text-[#3A3A38] inline-flex items-center gap-1"
                           data-testid="draft-sr-vehicle">
                        <Truck size={12} className="text-[#2D5A27] shrink-0" />
                        <span className="truncate font-mono">{sr.vehicle_no}</span>
                      </div>
                    )}
                    {sr.request_no && (
                      <div className="text-[11px] text-[#8A8780] inline-flex items-center gap-1 font-mono">
                        SR · {sr.request_no}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Field bag codes (read-only info) */}
              {(inv?.field_bag_codes || []).length > 0 && (
                <div className="text-[11px]" data-testid="draft-editor-bags">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1">
                    Field bags · {inv.field_bag_codes.length}
                  </div>
                  <div className="font-mono text-[#5C5A55] max-h-16 overflow-y-auto bg-[#F7F6F2] rounded p-2">
                    {inv.field_bag_codes.slice(0, 20).join(", ")}
                    {inv.field_bag_codes.length > 20 && ` +${inv.field_bag_codes.length - 20} more`}
                  </div>
                </div>
              )}

              {/* Waste weights (covered by package) */}
              {included.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
                    Adjust weights (kg)
                  </div>
                  {included.map((it) => (
                    <div key={it.waste_type_id} className="flex items-center gap-3 py-1.5 border-b border-[#F0EDE4] last:border-b-0" data-testid={`draft-line-${it.waste_type_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{it.waste_name}</div>
                        <div className="text-[10px] text-[#8A8780]">
                          {it.category} · {it.transaction_type === "PAY" ? "we pay ₹" : "customer pays ₹"}{it.customer_rate}/kg
                        </div>
                      </div>
                      <div className="w-24">
                        <input inputMode="decimal" value={weights[it.waste_type_id] ?? ""}
                                onChange={(e) => setWeights((w) => ({ ...w, [it.waste_type_id]: e.target.value }))}
                                placeholder="0.0"
                                data-testid={`draft-weight-${it.waste_type_id}`}
                                className="w-full h-10 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right"/>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {included.length === 0 && ctx.package && (
                <div className="text-[11px] text-[#C25D38] bg-[#F9EDE7] rounded p-2 inline-flex items-center gap-1.5">
                  <Warning size={12} />Package has no included waste categories.
                </div>
              )}

              {/* Ad-hoc waste items */}
              {adhocWaste.length > 0 && (
                <div data-testid="draft-adhoc-waste">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
                    Added waste items
                  </div>
                  {adhocWaste.map((line, idx) => {
                    const w = wasteById.get(line.waste_type_id);
                    return (
                      <div key={`${line.waste_type_id}-${idx}`}
                           className="flex items-center gap-2 py-1.5 border-b border-[#F0EDE4] last:border-b-0"
                           data-testid={`draft-adhoc-waste-${idx}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{w?.waste_name || line.waste_type_id}</div>
                          <div className="text-[10px] text-[#8A8780]">
                            {w?.category} · {w?.transaction_type === "PAY" ? "we pay ₹" : "customer pays ₹"}{w?.customer_rate || 0}/kg
                          </div>
                        </div>
                        <input inputMode="decimal" value={line.quantity_kg}
                                onChange={(e) => setAdhocWaste((rows) =>
                                  rows.map((r, i) => i === idx ? { ...r, quantity_kg: e.target.value } : r))}
                                placeholder="kg"
                                data-testid={`draft-adhoc-waste-qty-${idx}`}
                                className="w-20 h-9 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right"/>
                        <button onClick={() => removeAdhocWaste(idx)}
                                data-testid={`draft-adhoc-waste-remove-${idx}`}
                                className="p-1.5 rounded hover:bg-[#F9EDE7] text-[#C25D38]">
                          <Trash size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Products */}
              {products.length > 0 && (
                <div data-testid="draft-products">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
                    Products
                  </div>
                  {products.map((line, idx) => {
                    const p = productById.get(line.product_id);
                    return (
                      <div key={`${line.product_id}-${idx}`}
                           className="flex items-center gap-2 py-1.5 border-b border-[#F0EDE4] last:border-b-0"
                           data-testid={`draft-product-${idx}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{p?.name || line.product_id}</div>
                          <div className="text-[10px] text-[#8A8780]">
                            {p?.sku ? `${p.sku} · ` : ""}₹{p?.price || 0}{p?.unit ? ` / ${p.unit}` : ""}
                          </div>
                        </div>
                        <input inputMode="numeric" value={line.quantity}
                                onChange={(e) => setProducts((rows) =>
                                  rows.map((r, i) => i === idx ? { ...r, quantity: e.target.value } : r))}
                                placeholder="qty"
                                data-testid={`draft-product-qty-${idx}`}
                                className="w-20 h-9 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right"/>
                        <button onClick={() => removeProduct(idx)}
                                data-testid={`draft-product-remove-${idx}`}
                                className="p-1.5 rounded hover:bg-[#F9EDE7] text-[#C25D38]">
                          <Trash size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add-item action buttons */}
              <div className="flex gap-2">
                <button onClick={() => { setPickerOpen("waste"); setPickerQuery(""); }}
                        disabled={allWaste.length === 0}
                        data-testid="draft-add-waste-btn"
                        className="flex-1 h-9 border border-dashed border-[#2D5A27] rounded text-xs text-[#2D5A27] inline-flex items-center justify-center gap-1.5 hover:bg-[#F0F5EE] disabled:opacity-50 disabled:cursor-not-allowed">
                  <Plus size={12} weight="bold"/> Add waste item
                </button>
                <button onClick={() => { setPickerOpen("product"); setPickerQuery(""); }}
                        disabled={allProducts.length === 0}
                        data-testid="draft-add-product-btn"
                        className="flex-1 h-9 border border-dashed border-[#2D5A27] rounded text-xs text-[#2D5A27] inline-flex items-center justify-center gap-1.5 hover:bg-[#F0F5EE] disabled:opacity-50 disabled:cursor-not-allowed">
                  <Plus size={12} weight="bold"/> Add product
                </button>
              </div>

              {/* Extras */}
              {(ctx.extra_charges || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">Extra charges</div>
                  <div className="space-y-1.5">
                    {ctx.extra_charges.map((x) => (
                      <label key={x.id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={extras.includes(x.id)}
                                onChange={(e) => setExtras(
                                  e.target.checked ? [...extras, x.id]
                                    : extras.filter((y) => y !== x.id),
                                )}
                                data-testid={`draft-extra-${x.id}`}
                                className="w-4 h-4 accent-[#2D5A27]"/>
                        <span className="flex-1 truncate">{extraChargeLabel(x)}</span>
                        <span className="text-[#8A8780] font-mono">₹{x.amount}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Distance */}
              <div className="flex items-center gap-3">
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] flex-1">
                  Distance (km)
                </label>
                <input inputMode="decimal" value={distance}
                        onChange={(e) => setDistance(e.target.value)}
                        data-testid="draft-distance"
                        className="w-24 h-10 px-2 border border-[#E5E2D9] rounded text-sm font-mono text-right"/>
              </div>

              {/* Notes */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">Office notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                           data-testid="draft-notes"
                           placeholder="Reason for edit / adjustments…"
                           className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>

              {/* Live preview totals */}
              <div className="bg-[#0F1F0C] text-white rounded-md p-3 space-y-1" data-testid="draft-editor-preview">
                <div className="flex items-baseline justify-between">
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">
                    Live total {previewing && <span className="ml-1 opacity-50">…</span>}
                  </div>
                  <div className="font-heading text-xl">₹{Number((preview?.total ?? inv?.total) || 0).toFixed(2)}</div>
                </div>
                <div className="flex items-baseline justify-between text-[10px] opacity-70">
                  <span>Subtotal · GST</span>
                  <span className="font-mono">
                    ₹{Number((preview?.subtotal ?? inv?.subtotal) || 0).toFixed(2)}
                    {" · "}
                    ₹{Number((preview?.gst_amount ?? inv?.gst_amount) || 0).toFixed(2)}
                    {" (" + Number(preview?.gst_rate ?? inv?.gst_rate ?? 0) + "%)"}
                  </span>
                </div>
                {preview && preview.delta_total !== 0 && (
                  <div className={`flex items-baseline justify-between text-[11px] pt-1 border-t border-white/10 ${
                    preview.delta_total > 0 ? "text-[#F9E4B7]" : "text-[#D4E0D1]"
                  }`}>
                    <span className="opacity-80">Change vs original</span>
                    <span className="font-mono">
                      {preview.delta_total > 0 ? "+" : ""}₹{Number(preview.delta_total).toFixed(2)}
                    </span>
                  </div>
                )}
                <PrepaidSettlement prepaid={prepaid} total={preview?.total ?? inv?.total} dark testid="draft-prepaid"/>
              </div>
            </>
          )}
        </div>

        {/* Item picker overlay */}
        {pickerOpen && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4"
               onClick={() => setPickerOpen(null)}
               data-testid="draft-picker-overlay">
            <div className="bg-white rounded-md w-full max-w-md max-h-[70vh] flex flex-col overflow-hidden"
                 onClick={(e) => e.stopPropagation()}>
              <div className="p-3 border-b border-[#E5E2D9] flex items-center gap-2">
                <MagnifyingGlass size={14} className="text-[#8A8780]" />
                <input autoFocus value={pickerQuery}
                       onChange={(e) => setPickerQuery(e.target.value)}
                       placeholder={pickerOpen === "waste" ? "Search waste items…" : "Search products…"}
                       data-testid="draft-picker-search"
                       className="flex-1 text-sm outline-none" />
                <button onClick={() => setPickerOpen(null)}
                        data-testid="draft-picker-close"
                        className="p-1 rounded hover:bg-[#F7F6F2]">
                  <X size={14} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto" data-testid="draft-picker-list">
                {pickerResults.length === 0 && (
                  <div className="p-6 text-center text-xs text-[#8A8780]">No matches</div>
                )}
                {pickerOpen === "waste" && pickerResults.map((w) => (
                  <button key={w.waste_type_id} onClick={() => addWasteItem(w)}
                          data-testid={`draft-picker-waste-${w.waste_type_id}`}
                          className="w-full text-left px-3 py-2 border-b border-[#F0EDE4] hover:bg-[#F7F6F2]">
                    <div className="text-sm font-medium">{w.waste_name}</div>
                    <div className="text-[10px] text-[#8A8780]">
                      {w.category} · {w.transaction_type === "PAY" ? "we pay ₹" : "₹"}{w.customer_rate}/kg
                    </div>
                  </button>
                ))}
                {pickerOpen === "product" && pickerResults.map((p) => (
                  <button key={p.id} onClick={() => addProductItem(p)}
                          data-testid={`draft-picker-product-${p.id}`}
                          className="w-full text-left px-3 py-2 border-b border-[#F0EDE4] hover:bg-[#F7F6F2]">
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-[10px] text-[#8A8780]">
                      {p.sku ? `${p.sku} · ` : ""}₹{p.price}{p.unit ? ` / ${p.unit}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="p-5 border-t border-[#E5E2D9] shrink-0 space-y-2">
          <button onClick={() => save(true)} disabled={busy || loading}
                   data-testid="draft-save-approve"
                   className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <CheckCircle size={14} weight="fill"/>
            {busy ? "Saving…" : "Save & approve"}
            <ArrowRight size={12} weight="bold" />
          </button>
          <button onClick={() => save(false)} disabled={busy || loading}
                   data-testid="draft-save"
                   className="w-full h-11 border border-[#E5E2D9] hover:bg-[#F7F6F2] disabled:opacity-60 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <Receipt size={14}/>
            {busy ? "Saving…" : "Save changes only"}
          </button>
        </div>
      </div>
    </div>
  );
}
